/*
  <as-seen-on-scroller> — a native horizontal scroll container that also
  auto-scrolls on its own. Used by the "As seen on" strip (homepage) and the
  product-page "As seen on" row so the two behave identically:

    - The element itself is the scroll container (overflow-x: auto), so users can
      always move it left/right by hand — touch swipe, trackpad, or mouse drag.
    - When the content overflows, it gently auto-scrolls back and forth at
      data-aso-speed pixels per second.
    - Auto-scroll pauses while the pointer is over the strip, while a hotspot
      tooltip is open, for a moment after any manual interaction, and while the
      strip is off-screen. It respects prefers-reduced-motion.

  Unlike the theme's scrolling-text marquee this keeps every card a real DOM
  node (no cloning), so the product hotspots stay wired and interactive.
*/
if (typeof customElements !== "undefined" && typeof customElements.get("as-seen-on-scroller") === "undefined") {
  class AsSeenOnScroller extends HTMLElement {
    connectedCallback() {
      if (this._wired) { return; }
      this._wired = true;

      this.speed = parseFloat(this.dataset.asoSpeed) || 20; // pixels per second
      this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.direction = 1; // 1 = towards the end, -1 = back towards the start
      this.running = false;
      this.visible = true;
      this.lastTs = null;
      this.resumeAt = 0; // auto-scroll stays paused until this timestamp
      this._scrollable = null;
      this._pos = null; // our own float scroll position (see _tick)

      this._tick = this._tick.bind(this);
      this._bindInteraction();

      // Reduced-motion visitors never see the auto-scroll, so don't spin up a
      // 60fps rAF loop (and its visibility observer) that would only ever
      // no-op. Just keep the is-scrollable affordance in sync on connect and
      // whenever the viewport resizes.
      if (this.reduced) {
        this._syncScrollable();
        this._onResize = this._syncScrollable.bind(this);
        window.addEventListener("resize", this._onResize);
      } else {
        this._observeVisibility();
        this._start();
      }
    }

    disconnectedCallback() {
      this._stop();
      if (this._io) { this._io.disconnect(); }
      if (this._onResize) { window.removeEventListener("resize", this._onResize); }
      if (this._onDocPress) { document.removeEventListener("click", this._onDocPress, true); }
      if (this._onDocKey) { document.removeEventListener("keydown", this._onDocKey, true); }
      if (this._onDesignSync) {
        document.removeEventListener("shopify:block:select", this._onDesignSync);
        document.removeEventListener("shopify:block:deselect", this._onDesignSync);
      }
    }

    _isPaused(now) {
      if (this._pointerOverImage) { return true; }
      if (this._dragging) { return true; }
      // A control inside the strip has KEYBOARD focus (Tab landed on a hotspot
      // dot or the IG credit link) — hold still so the loop doesn't nudge it
      // back out from under the browser's scroll-into-view. Read live from
      // activeElement + :focus-visible rather than a sticky flag, so a mouse or
      // touch that leaves focus on a dot after closing its tooltip can't wedge
      // auto-scroll paused forever.
      if (this._hasKeyboardFocusInside()) { return true; }
      if (now < this.resumeAt) { return true; }
      // A hotspot tooltip is open — hold still so the card doesn't slide away.
      if (this.querySelector(".image-hotspots__spot--bullet.active")) { return true; }
      return false;
    }

    _hasKeyboardFocusInside() {
      var a = document.activeElement;
      if (!a || a === document.body || !this.contains(a)) { return false; }
      // :focus-visible is set only for keyboard focus, so a click/tap that
      // leaves focus sitting on a dot (tooltip already closed) won't pause us.
      try { return a.matches(":focus-visible"); } catch (e) { return true; }
    }

    // The strip only carries its tooltip headroom (see section-as-seen-on.css)
    // while a hotspot tooltip is open or focus sits inside a spot — at rest it
    // must be a plain interactive scroll container or iOS won't touch-scroll
    // it. The padding + negative margin cancel out, so flipping the class
    // moves nothing on screen.
    _syncActiveSpot() {
      var a = document.activeElement;
      var focused = !!(a && a !== document.body && this.contains(a) && a.closest(".image-hotspots__spot"));
      var open = !!this.querySelector(".image-hotspots__spot--bullet.active");
      this.classList.toggle("has-active-spot", open || focused);
    }

    // Toggle the .is-scrollable affordance to match whether the content
    // overflows. Factored out of _tick so the reduced-motion path (which never
    // runs _tick) can keep it in sync on connect and on resize.
    _syncScrollable() {
      var overflow = (this.scrollWidth - this.clientWidth) > 2;
      if (overflow !== this._scrollable) {
        this._scrollable = overflow;
        this.classList.toggle("is-scrollable", overflow);
      }
    }

    _tick(ts) {
      if (!this.running) { return; }
      if (this.lastTs === null) { this.lastTs = ts; }
      var dt = ts - this.lastTs;
      this.lastTs = ts;
      // Clamp the step if rAF stalled (tab hidden, touch elsewhere) so we ease
      // back in instead of teleporting to the far end.
      if (dt > 100) { dt = 100; }

      this._syncScrollable();
      var overflow = this._scrollable;

      if (overflow && !this.reduced && this.visible && !this._isPaused(ts)) {
        var max = this.scrollWidth - this.clientWidth;
        // Track the position as a float ourselves rather than reading it back
        // from scrollLeft each frame. iOS Safari reports scrollLeft as a rounded
        // integer, so a sub-pixel per-frame step (~0.58px at 35px/s@60fps) would
        // truncate to 0 on every write and the strip would never move. If the
        // user scrolled by hand, scrollLeft has drifted from our tracked value —
        // resync so auto-scroll continues from where they left off.
        if (this._pos === null || Math.abs(this._pos - this.scrollLeft) > 1) {
          this._pos = this.scrollLeft;
        }
        this._pos += (this.direction * this.speed * dt) / 1000;
        if (this._pos >= max) { this._pos = max; this.direction = -1; }
        else if (this._pos <= 0) { this._pos = 0; this.direction = 1; }
        this.scrollLeft = this._pos;
      }

      window.requestAnimationFrame(this._tick);
    }

    _start() {
      if (this.running) { return; }
      this.running = true;
      this.lastTs = null;
      window.requestAnimationFrame(this._tick);
    }

    _stop() { this.running = false; }

    _nudge(ms) {
      // Keep auto-scroll paused for a short while after any manual interaction.
      this.resumeAt = performance.now() + (ms || 1500);
    }

    _bindInteraction() {
      var self = this;

      // Pause on hover, but ONLY while the pointer is over a photo (not the gaps
      // between cards, the @handle credit line, or the tooltip headroom). Uses
      // pointerover/out delegation so moving between a photo's own children
      // (image <-> hotspot dot <-> open tooltip, all inside the wrapper) keeps
      // it paused, while moving off the photo resumes it. Touch has no hover, so
      // it's ignored here (touchstart/move handle the pause on touch).
      var PHOTO = ".image-with-hotspots-wrapper";
      var overPhoto = function (node) {
        return !!(node && node.closest && node.closest(PHOTO));
      };
      this.addEventListener("pointerover", function (e) {
        if (e.pointerType === "touch") { return; }
        if (overPhoto(e.target)) { self._pointerOverImage = true; }
      });
      this.addEventListener("pointerout", function (e) {
        if (e.pointerType === "touch") { return; }
        // relatedTarget is where the pointer is heading; still on a photo -> stay paused.
        if (!overPhoto(e.relatedTarget)) { self._pointerOverImage = false; }
      });
      this.addEventListener("pointerleave", function () { self._pointerOverImage = false; });

      // Tooltip headroom (.has-active-spot, see _syncActiveSpot). Class flips
      // ride on document-level CAPTURE listeners: capture runs before the
      // hotspot handlers (which stopPropagation), and the setTimeout lets
      // component-image-hotspots settle .active — whichever way it toggled —
      // before we read it. A press that lands on a spot pre-adds the class so
      // the opening tooltip never paints clipped, even for one frame; when
      // nothing is open and the press is elsewhere there is nothing to do, so
      // skip the timeout entirely.
      var syncSoon = function () { window.setTimeout(function () { self._syncActiveSpot(); }, 0); };
      this._onDocPress = function (e) {
        var onSpot = e.target instanceof Element && self.contains(e.target) &&
          e.target.closest(".image-hotspots__spot");
        if (onSpot) { self.classList.add("has-active-spot"); }
        else if (!self.classList.contains("has-active-spot")) { return; }
        syncSoon();
      };
      this._onDocKey = function (e) {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") { self._onDocPress(e); }
      };
      document.addEventListener("click", this._onDocPress, true);
      document.addEventListener("keydown", this._onDocKey, true);
      // Keyboard focus on a dot needs the headroom too, so its focus ring (and
      // a tooltip opened with Enter/Space) isn't clipped at the strip's edge.
      this.addEventListener("focusin", this._onDocPress);
      // The theme editor opens tooltips via block selection, without any click.
      if (window.Shopify && Shopify.designMode) {
        this._onDesignSync = syncSoon;
        document.addEventListener("shopify:block:select", this._onDesignSync);
        document.addEventListener("shopify:block:deselect", this._onDesignSync);
      }

      // The pause-while-focused behaviour is derived live from activeElement in
      // _isPaused() (keyboard focus only), so there is no sticky flag that can
      // get wedged. focusout still gives a short grace nudge so the strip does
      // not snap-resume the instant focus moves away mid-interaction, and the
      // headroom re-syncs once focus has settled somewhere else.
      this.addEventListener("focusout", function () { self._nudge(); syncSoon(); });

      // Trackpad / mouse-wheel horizontal scrolling counts as a manual nudge.
      // Only when the gesture is mostly horizontal, so scrolling the page
      // vertically past the strip doesn't pause it. We deliberately do NOT
      // listen for "scroll": our own per-frame scrollLeft writes fire that
      // event, which would re-pause the auto-scroll every frame and freeze it.
      this.addEventListener("wheel", function (e) {
        if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) { self._nudge(); }
      }, { passive: true });

      // Touch swipe uses native horizontal scrolling; pause while a finger is
      // down or dragging, then resume shortly after it lifts. iOS momentum can
      // keep scrolling well past the default 1500ms pause, and writing
      // scrollLeft mid-momentum cancels it with a visible stutter — so anchor a
      // longer pause to touchend/touchcancel (when the finger actually lifts)
      // rather than only the last touchmove, giving momentum room to settle.
      this.addEventListener("touchstart", function () { self._nudge(); }, { passive: true });
      this.addEventListener("touchmove", function () { self._nudge(); }, { passive: true });
      this.addEventListener("touchend", function () { self._nudge(2500); }, { passive: true });
      this.addEventListener("touchcancel", function () { self._nudge(2500); }, { passive: true });

      // Drag-to-scroll for mouse users. Touch and trackpad use native scrolling.
      var startX = 0, startLeft = 0, down = false, moved = false, pid = null;
      // px of pointer travel before a press counts as a drag (not a click).
      // 8px (vs a trigger-happy 5) keeps mouse jitter on a genuine click from
      // being misread as a drag and swallowing the click on a hotspot / IG link.
      var DRAG_THRESHOLD = 8;

      this.addEventListener("pointerdown", function (e) {
        if (e.pointerType === "touch") { return; }
        down = true; moved = false; pid = e.pointerId;
        startX = e.clientX; startLeft = self.scrollLeft;
        self._nudge();
      });

      this.addEventListener("pointermove", function (e) {
        if (!down) { return; }
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < DRAG_THRESHOLD) { return; } // small movements are clicks, not drags
        moved = true;
        self._dragging = true;
        self.classList.add("is-dragging");
        try { self.setPointerCapture(pid); } catch (err) { /* ignore */ }
        self.scrollLeft = startLeft - dx;
        self._nudge();
        e.preventDefault();
      });

      var endDrag = function () {
        if (!down) { return; }
        down = false;
        self._dragging = false;
        self.classList.remove("is-dragging");
        if (moved) {
          self._nudge();
          // Swallow the click a drag would otherwise fire, so dragging across a
          // hotspot dot doesn't pop its tooltip open.
          self._swallowClick = true;
          window.setTimeout(function () { self._swallowClick = false; }, 0);
        }
      };
      this.addEventListener("pointerup", endDrag);
      this.addEventListener("pointercancel", endDrag);

      this.addEventListener("click", function (e) {
        if (self._swallowClick) { e.stopPropagation(); e.preventDefault(); }
      }, true);
    }

    _observeVisibility() {
      var self = this;
      if (!("IntersectionObserver" in window)) { return; }
      this._io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible) { self._start(); } else { self._stop(); }
      });
      this._io.observe(this);
    }
  }

  customElements.define("as-seen-on-scroller", AsSeenOnScroller);
}
